import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DateRange } from "react-day-picker"
import { Controller, FieldError, useForm, UseFormSetValue, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { ListPlus } from "@phosphor-icons/react"
import { Editor as ToastUiEditor } from "@toast-ui/react-editor"
import { z } from "zod"
import { useUser } from "@/apis/bff"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { CardBodyContent, CardBodyHeader, CardBodyHeaderTitle } from "@/components/ui/card"
import { DateRangePicker } from "@/components/ui/date-range-picker"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Editor, EditorView } from "@/components/ui/editor"
import { FormStatus } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchInput, SuggestionSearch } from "@/components/ui/search"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useClickOutside } from "@/hooks/useClickOutside"
import { useEditorKeyDown } from "@/hooks/useEditorKeyDown"
import { useSofiaEventListener } from "@/hooks/useSofiaEventListener"
import { DateOnly } from "@/lib/date-only"
import { SearchOption } from "@/modules/patients/components/search-option"
import { isPrescriptionRequestType } from "@/modules/patients/detail/episode-of-care/episode-of-care-dropdown"
import { useTreatmentPlanTemplatesByIcdCodeQuery } from "@/modules/patients/detail/episode-of-care/services/useTreatmentPlanTemplatesByIcdCodeQuery"
import { ProgressNoteReadOnlyCardSection } from "@/modules/patients/detail/progress-note/components/progress-note-readonly-card-section"
import { useProgressNoteTreatmentPlanForm } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteTreatmentPlanForm"
import { useProgressNoteTreatmentPlanFormEffect } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteTreatmentPlanFormEffect"
import { isRecommendedMedicationsEmpty } from "@/modules/patients/detail/progress-note/lib/is-recommended-medications-empty"
import { matchesAsyncRequestStatus } from "@/modules/patients/detail/progress-note/lib/matches-async-request-status"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useTreatmentPlanTemplatesData } from "@/modules/patients/hooks/useTreatmentPlanTemplatesData"
import { areDocumentsEmpty } from "@/modules/patients/libs/areEmptyDocuments"
import { moveFocusToNextElement } from "@/modules/patients/libs/move-focus-to-next-element"
import { Ch_updateProgressNoteTreatmentPlanInput } from "@/modules/patients/services/ch/__generated__/chUpdateProgressNoteTreatmentPlanMutation.graphql"
import { De_updateProgressNoteTreatmentPlanInput } from "@/modules/patients/services/de/__generated__/deUpdateProgressNoteTreatmentPlanMutation.graphql"
import { TreatmentPlanTemplateStoreData } from "@/modules/patients/services/fetchTreatmentPlanTemplates"
import { useUpdateProgressNoteTreatmentPlanMutation } from "@/modules/patients/services/useUpdateProgressNoteTreatmentPlanMutation"
import { AsyncRequestStatus, Country, Language, PointOfCareType } from "@/types/__generated__/union-types"

export const formDirtySchema = z.object({ formIsDirty: z.boolean().optional() })
export type FormDirtySchemaType = z.infer<typeof formDirtySchema>

const TreatmentPlanSchema = z
  .object({
    id: z.string(),
    title: z
      .string({ error: "common.fieldErrorList.requiredField" })
      .max(50)
      .refine((val) => !isEmptyOrWhitespace(val), { message: "common.fieldErrorList.requiredField" }),
    recommended: z.string().nullable(),
    followUpInstructions: z.string().nullable(),
    period: z.custom<DateRange>().refine((val) => val.from && val.to, {
      message: "common.fieldErrorList.requiredField",
    }),
    isTreatmentPlanOptional: z.boolean().default(false), // new field to display treatment error or not
  })
  .extend(formDirtySchema.shape)

const isEmptyOrWhitespace = (value: string | null | undefined): boolean => !value || value.trim().length === 0
const isValidDateRange = (from?: string, to?: string): boolean => !isEmptyOrWhitespace(from) && !isEmptyOrWhitespace(to)

export const isTreatmentPlanSectionOptional = (data: EpisodeOfCareProgressNoteData): boolean => {
  if (!data.pointOfCare) return false

  const pointOfCareValue = data.pointOfCare.actual ?? data.pointOfCare.medical

  return isRecommendedMedicationsEmpty(data) && pointOfCareValue === "TELECARE"
    ? areDocumentsEmpty(data.__typename === "ch_ProgressNote" ? "CH" : "DE", data)
    : pointOfCareValue === "INCOMPLETE_TELECONSULTATION"
}

export const excludedPointOfCare: PointOfCareType[] = ["INCOMPLETE_TELECONSULTATION", "TELECARE"]

export type TreatmentPlanFormType = z.infer<typeof TreatmentPlanSchema>
export type TreatmentPlanFormInput = z.input<typeof TreatmentPlanSchema>
export type TreatmentPlan = ProgressNoteAndParentData["episodeOfCareProgressNoteData"]["treatmentPlan"]

const TreatmentPlanEditableSection = ({
  episodeOfCareProgressNoteData,
  diagnosisCode,
}: {
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  diagnosisCode: string
}) => {
  const { t } = useTranslation()
  const country = useCountryParam()
  const recommendedRef = useRef<ToastUiEditor>(null)
  const followUpInstructionsRef = useRef<ToastUiEditor>(null)
  const {
    id,
    treatmentPlan,
    appointment: { language: appointmentLanguage, channel, requestType, asyncRequestStatus },
  } = episodeOfCareProgressNoteData

  const showSofiaSuggestions = diagnosisCode.length > 0
  const isAsyncPrescription = channel === "ASYNC_REQUEST" && isPrescriptionRequestType(requestType)
  const { defaultValues, storeFormState } = useProgressNoteTreatmentPlanForm(
    id,
    treatmentPlan,
    episodeOfCareProgressNoteData,
  )
  const [updateProgressNoteTreatmentPlan] = useUpdateProgressNoteTreatmentPlanMutation(country)

  const patientData = usePatientFileContext((state) => state.patientData)
  const contactChannel = patientData?.contactChannel

  const { isOneOf } = useUser()
  const shouldShowNoCommunicationChannelWarning =
    isOneOf(["Doctor", "MedicalSupervisor", "NursingProfessional"]) && contactChannel === "NO_CHANNEL"

  const form = useForm<TreatmentPlanFormInput, unknown, TreatmentPlanFormType>({
    defaultValues,
    resolver: zodResolver(TreatmentPlanSchema),
    mode: "onChange",
  })

  useProgressNoteTreatmentPlanFormEffect(id, episodeOfCareProgressNoteData, storeFormState, form)

  const { handleSubmit, control, reset, formState, getValues, setValue } = form

  const currentRecommendedValue = useWatch({
    control,
    name: "recommended",
    defaultValue: defaultValues.recommended,
  })

  const currentFollowUpInstructionsValue = useWatch({
    control,
    name: "followUpInstructions",
    defaultValue: defaultValues.followUpInstructions,
  })

  // formState should ALWAYS be deconstructed, as per documentation: https://react-hook-form.com/docs/useformstate
  const { isDirty, isSubmitSuccessful } = formState

  const handleUpdateProgressNoteTreatmentPlan = useCallback(() => {
    handleSubmit(async (formData) => {
      const { period, title, id, followUpInstructions, recommended } = formData
      if (!period.from || !period.to) return

      const normalizeTextField = (text: string | null | undefined) => {
        if (!text) return null
        const trimmed = text.trim()
        return trimmed.length === 0 ? null : trimmed
      }

      const input: Ch_updateProgressNoteTreatmentPlanInput & De_updateProgressNoteTreatmentPlanInput = {
        id,
        followUpInstructions: normalizeTextField(followUpInstructions),
        recommended: normalizeTextField(recommended),
        title,
        from: new DateOnly(period.from).toString(),
        to: new DateOnly(period.to).toString(),
      }

      await new Promise((resolve, reject) => updateProgressNoteTreatmentPlan(input, resolve, reject))
    })()
  }, [handleSubmit, updateProgressNoteTreatmentPlan])

  const updateWhenDirty = useCallback(() => {
    if (!isDirty) return

    handleUpdateProgressNoteTreatmentPlan()
  }, [handleUpdateProgressNoteTreatmentPlan, isDirty])

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getValues(), { keepSubmitCount: true, keepIsSubmitted: true, keepValues: true })
    }
  }, [isSubmitSuccessful, getValues, reset])

  useSofiaEventListener("TREATMENT_PLAN_SUGGESTION_APPEND_EVENT", (event) => {
    // Only handle events for this specific progress note
    if (event.detail.progressNoteId === id) {
      handleUpdateProgressNoteTreatmentPlan()
    }
  })

  const isTreatmentPlanOptional = useMemo(
    () => isTreatmentPlanSectionOptional(episodeOfCareProgressNoteData),
    [episodeOfCareProgressNoteData],
  )

  useEffect(() => {
    setValue("isTreatmentPlanOptional", isTreatmentPlanOptional, { shouldDirty: false })
  }, [isTreatmentPlanOptional, setValue])

  const currentPeriodValue = useWatch({ control, name: "period" })

  const titleWatch = useWatch({ control, name: "title" })
  const recommendedWatch = useWatch({ control, name: "recommended" })
  const followUpInstructionsWatch = useWatch({ control, name: "followUpInstructions" })

  const isCollapseTreatmentPlan = useMemo(() => {
    return (
      !isEmptyOrWhitespace(titleWatch) ||
      !isEmptyOrWhitespace(recommendedWatch) ||
      !isEmptyOrWhitespace(followUpInstructionsWatch)
    )
  }, [titleWatch, recommendedWatch, followUpInstructionsWatch])

  const handleError = (error: FieldError | undefined) => {
    if (error && error.type === "too_big") {
      return t(error.message ?? "", { max: 50 })
    }

    if (!error || getValues("isTreatmentPlanOptional")) return ""

    return t(error.message ?? "")
  }

  useEditorKeyDown(recommendedRef, isCollapseTreatmentPlan)
  useEditorKeyDown(followUpInstructionsRef, isCollapseTreatmentPlan)

  return (
    <>
      <CardBodyHeader data-testid="treatment-plan-title">
        <CardBodyHeaderTitle>{t("episodeOfCare.progressNote.treatmentPlan.headerTitle")}</CardBodyHeaderTitle>
        <div className="self-center pr-4">
          <FormStatus formControl={form} />
        </div>
      </CardBodyHeader>
      <CardBodyContent className="grid gap-4" data-testid="treatment-plan-content">
        {shouldShowNoCommunicationChannelWarning && (
          <Banner type="inline" variant="critical">
            <p>{t("patient.information.contact.noChannelWarning")}</p>
          </Banner>
        )}
        <div className="grid w-full gap-4 @lg/patient-layout:grid-cols-2">
          <Controller
            name="title"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TreatmentPlanTitle
                currentRecommendedValue={currentRecommendedValue}
                currentFollowUpInstructionsValue={currentFollowUpInstructionsValue}
                currentPeriodValue={currentPeriodValue}
                setFormValue={setValue}
                language={appointmentLanguage}
                subject={value}
                country={country}
                diagnosisCode={diagnosisCode}
                asyncRequestStatus={asyncRequestStatus}
                isAsyncPrescription={isAsyncPrescription}
                showSofiaSuggestions={showSofiaSuggestions}
                onTemplateSelect={(value) => {
                  onChange(value)
                  handleUpdateProgressNoteTreatmentPlan()
                }}
                onSubjectChange={onChange}
                onSubjectBlur={updateWhenDirty}
                error={handleError(error)}
              />
            )}
          />
          <Controller
            name="period"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TreatmentPeriod
                value={value}
                onChange={(val) => {
                  onChange(val)
                  updateWhenDirty()
                }}
                onClose={() => {
                  FaroInsightsWrapper.pushEvent("treatmentplan_define_period")
                }}
                error={handleError(error)}
              />
            )}
          />
        </div>
        {isCollapseTreatmentPlan && (
          <div className="grid gap-4">
            <div className="grid gap-2" data-testid="treatment-plan-recommended-treatment">
              <span className="text-on-surface-subdued text-xs">
                {t("episodeOfCare.progressNote.treatmentPlan.recommendedTreatment")}
              </span>
              <Controller
                control={control}
                name="recommended"
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                  <Editor
                    ref={recommendedRef}
                    minHeight="100px"
                    autofocus={false}
                    value={value}
                    name={name}
                    onChange={onChange}
                    onFocus={() => {
                      FaroInsightsWrapper.pushEvent("treatment_plan_recommended_focus")
                    }}
                    onBlur={() => {
                      FaroInsightsWrapper.pushEvent("treatment_plan_recommended_blur")
                      updateWhenDirty()
                    }}
                    error={error?.message}
                  />
                )}
              />
            </div>
            <div className="grid gap-2" data-testid="treatment-plan-follow-up-instructions">
              <span className="text-on-surface-subdued text-xs">
                {t("episodeOfCare.progressNote.treatmentPlan.followUpInstructions")}
              </span>
              <Controller
                control={control}
                name="followUpInstructions"
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                  <Editor
                    ref={followUpInstructionsRef}
                    minHeight="100px"
                    autofocus={false}
                    value={value}
                    name={name}
                    onChange={onChange}
                    onFocus={() => {
                      FaroInsightsWrapper.pushEvent("treatment_plan_followup_focus")
                    }}
                    onBlur={() => {
                      FaroInsightsWrapper.pushEvent("treatment_plan_followup_blur")
                      updateWhenDirty()
                    }}
                    error={error?.message}
                  />
                )}
              />
            </div>
          </div>
        )}
      </CardBodyContent>
    </>
  )
}

type TreatmentPlanTitleProps = {
  subject: string
  onTemplateSelect: (value: string) => void
  onSubjectChange: (value: string) => void
  onSubjectBlur: () => void
  onSubjectFocus?: () => void
  onOpenSubjectDropdown?: () => void
  setFormValue: UseFormSetValue<TreatmentPlanFormInput>
  currentRecommendedValue: string | null
  currentFollowUpInstructionsValue: string | null
  currentPeriodValue: DateRange
  language: Language
  country: Country
  diagnosisCode: string
  asyncRequestStatus: AsyncRequestStatus | null
  isAsyncPrescription: boolean
  showSofiaSuggestions: boolean
  error?: string
}

const TreatmentPlanTitle = ({
  subject = "",
  onTemplateSelect,
  onSubjectChange,
  onSubjectBlur,
  onSubjectFocus,
  onOpenSubjectDropdown,
  setFormValue,
  currentRecommendedValue,
  currentFollowUpInstructionsValue,
  currentPeriodValue,
  language,
  country,
  diagnosisCode,
  asyncRequestStatus,
  isAsyncPrescription,
  showSofiaSuggestions,
  error,
}: TreatmentPlanTitleProps) => {
  const { t } = useTranslation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)
  const treatmentPlanTemplateButtonRef = useRef<HTMLButtonElement>(null)
  const normalizedSearchValue = searchValue.length >= 3 ? searchValue.trim().toLowerCase() : ""
  const treatmentPlanTitleOptions = useTreatmentPlanTemplatesData(language)

  const matchesSearch = (option: TreatmentPlanTemplateStoreData): boolean =>
    option.title.toLowerCase().includes(normalizedSearchValue) ||
    (option.synonym?.toLowerCase().includes(normalizedSearchValue) ?? false)

  const eligibleTreatmentPlanTemplates = (treatmentPlanTitleOptions ?? [])
    .filter(matchesSearch)
    .sort((a, b) => a.title.localeCompare(b.title))

  const computePeriodFromDays = (amountOfDays: number): DateRange => {
    const from = new Date()
    const to = new Date(from)
    to.setDate(to.getDate() + amountOfDays)
    return { from, to }
  }

  const updateSubject = (value: string, type: "replace" | "add") => {
    if (type === "replace" || subject.trim().length === 0) {
      onTemplateSelect(value)
    } else {
      onTemplateSelect(`${subject}, ${value}`)
    }
  }

  const hasSomeEligibleTreatmentPlanTemplates = eligibleTreatmentPlanTemplates.length > 0
  const hasAnyTemplates = showSofiaSuggestions || hasSomeEligibleTreatmentPlanTemplates

  useClickOutside((event: MouseEvent) => {
    if (dropdownOpen && searchResultRef.current && !searchResultRef.current.contains(event.target as HTMLElement)) {
      setDropdownOpen(false)
    }
  }, searchRef)

  const handleReplaceTemplate = (option: TreatmentPlanTemplateStoreData) => {
    setSearchValue("")
    setDropdownOpen(false)

    setFormValue("recommended", option.recommendedTreatment ?? " ", { shouldDirty: true })
    setFormValue("followUpInstructions", option.followUpInstructions ?? " ", { shouldDirty: true })

    if (option.amountOfDays) {
      setFormValue("period", computePeriodFromDays(option.amountOfDays), { shouldDirty: true })
    }

    updateSubject(option.title, "replace")

    FaroInsightsWrapper.pushEvent("treatment_plan_template_replace")
  }

  const handleAddTemplateBelow = (option: TreatmentPlanTemplateStoreData) => {
    setSearchValue("")
    setDropdownOpen(false)

    setFormValue("recommended", appendTemplateValue(currentRecommendedValue, option.recommendedTreatment), {
      shouldDirty: true,
    })
    setFormValue(
      "followUpInstructions",
      appendTemplateValue(currentFollowUpInstructionsValue, option.followUpInstructions),
      { shouldDirty: true },
    )

    if (option.amountOfDays && !currentPeriodValue?.to) {
      setFormValue("period", computePeriodFromDays(option.amountOfDays), { shouldDirty: true })
    }

    updateSubject(option.title, "add")

    FaroInsightsWrapper.pushEvent("treatment_plan_template_add")
  }

  const handleKeyDownClearButton = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.code === "Enter" || e.code === "Space") {
      e.preventDefault()
      searchRef.current?.focus()
      setSearchValue("")
    }

    if (e.key === "Tab" && !e.shiftKey) {
      treatmentPlanTemplateButtonRef.current?.click()
      moveFocusToNextElement(treatmentPlanTemplateButtonRef.current)
    }
  }

  const renderTemplateItem = (option: TreatmentPlanTemplateStoreData) => (
    <DropdownMenuSub key={option.name}>
      <DropdownMenuSubTrigger
        className="group px-2 py-0"
        data-testid={option.name}
        onKeyDown={(e) => {
          if (e.code === "Enter") {
            handleReplaceTemplate(option)
            setTimeout(() => moveFocusToNextElement(treatmentPlanTemplateButtonRef.current), 50)
          }
        }}>
        <SearchOption
          className="group-data-highlighted:bg-surface-hovered block w-84.25"
          value={option.name}
          searchValue={searchValue}
          title={option.title}
          synonym={option.synonym}
          onSelect={() => handleReplaceTemplate(option)}
        />
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          data-testid={`${option.name}-action-buttons`}
          className="grid max-h-(--radix-dropdown-menu-content-available-height) gap-0.5"
          hideWhenDetached={true}>
          <DropdownMenuItem
            data-testid="replace-template-button"
            className="text-on-surface hover:bg-surface-hovered! active:bg-primary-surface-focused! h-8 rounded-md transition-all duration-300"
            onClick={() => handleReplaceTemplate(option)}>
            {t("episodeOfCare.progressNote.treatmentPlan.replaceExisting")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="add-template-button"
            className="text-on-surface hover:bg-surface-hovered! active:bg-primary-surface-focused! h-8 rounded-md transition-all duration-300"
            onClick={() => handleAddTemplateBelow(option)}>
            {t("episodeOfCare.progressNote.treatmentPlan.addBelow")}
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )

  return (
    <SuggestionSearch>
      <Input
        name="title"
        testId="treatment-plan-subject"
        label={t("common.subject")}
        value={subject}
        onChange={({ target: { value } }) => {
          onSubjectChange(value)
        }}
        onFocus={() => {
          FaroInsightsWrapper.pushEvent("treatment_plan_subject_focus")
          onSubjectFocus?.()
        }}
        onBlur={() => {
          FaroInsightsWrapper.pushEvent("treatment_plan_subject_blur")
          onSubjectBlur?.()
        }}
        className="pr-10"
        error={error}>
        <DropdownMenu
          open={dropdownOpen}
          onOpenChange={(isOpen) => {
            setDropdownOpen(isOpen)
            if (isOpen) {
              onOpenSubjectDropdown?.()
            }
          }}
          modal={false}>
          <DropdownMenuTrigger asChild ref={treatmentPlanTemplateButtonRef}>
            <Button
              data-testid="treatment-plan-template-selection-button"
              onClick={() => {
                FaroInsightsWrapper.pushEvent("treatment_plan_template_open")
              }}
              variant="unstyle"
              className="text-on-surface-subdued hover:bg-surface-hovered absolute top-1 right-2 p-2">
              <ListPlus className="text-on-surface-subdued h-6 w-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="rounded-lg" align="start" dataTestId="treatment_template_search_dialog">
            <div className="border-border border-b p-2">
              <SearchInput
                ref={searchRef}
                onKeyDown={(e) => {
                  e.stopPropagation()
                }}
                data-testid="find-treatment-plan-template-input"
                value={searchValue}
                placeholder={t("common.search")}
                autoComplete="off"
                onChange={({ target: { value } }) => {
                  setSearchValue(value)
                }}
                onClear={() => {
                  setSearchValue("")
                }}
                onClearButtonKeyDown={handleKeyDownClearButton}
              />
            </div>
            {hasAnyTemplates ? (
              <ScrollArea
                className="grid max-w-88.25 gap-0.5"
                data-testid="treatment-plan-template-scroll-section"
                viewportClassName="max-h-84">
                <div className="py-4">
                  {showSofiaSuggestions && (
                    <Suspense fallback={<SofiaSuggestionsSkeleton />}>
                      <SofiaSuggestionsSection
                        diagnosisCode={diagnosisCode}
                        country={country}
                        language={language}
                        asyncRequestStatus={asyncRequestStatus}
                        isAsyncPrescription={isAsyncPrescription}
                        hasRegularTemplates={hasSomeEligibleTreatmentPlanTemplates}
                        matchesSearch={matchesSearch}
                        renderTemplateItem={renderTemplateItem}
                      />
                    </Suspense>
                  )}
                  {hasSomeEligibleTreatmentPlanTemplates && (
                    <>
                      {showSofiaSuggestions && <DropdownMenuSeparator className="border-border my-2 border-t" />}
                      {eligibleTreatmentPlanTemplates.map(renderTemplateItem)}
                    </>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div
                className="text-on-surface-subdued relative cursor-default px-4 py-4 text-sm select-none"
                data-testid="nothing_found_treatment_plan_template">
                {t("common.nothingFound")}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </Input>
    </SuggestionSearch>
  )
}

type SofiaSuggestionsSectionProps = {
  diagnosisCode: string
  country: Country
  language: Language
  asyncRequestStatus: AsyncRequestStatus | null
  isAsyncPrescription: boolean
  hasRegularTemplates: boolean
  matchesSearch: (option: TreatmentPlanTemplateStoreData) => boolean
  renderTemplateItem: (option: TreatmentPlanTemplateStoreData) => React.ReactNode
}

const SofiaSuggestionsSkeleton = () => {
  const { t } = useTranslation()
  return (
    <div data-testid="sofia-supported-suggestions-skeleton">
      <div className="text-on-surface-subdued px-2 pb-2 text-sm leading-5 font-medium">
        {t("episodeOfCare.progressNote.sofiaSupportedSuggestions")}
      </div>
      <div className="grid gap-1 px-2">
        <div className="bg-surface-pressed h-8 animate-pulse rounded-md" />
      </div>
    </div>
  )
}

const SofiaSuggestionsSection = ({
  diagnosisCode,
  country,
  language,
  asyncRequestStatus,
  isAsyncPrescription,
  hasRegularTemplates,
  matchesSearch,
  renderTemplateItem,
}: SofiaSuggestionsSectionProps) => {
  const { t } = useTranslation()
  const sofiaTemplates = useTreatmentPlanTemplatesByIcdCodeQuery(diagnosisCode, country)

  const eligibleSofiaSuggestions = sofiaTemplates
    .filter((template) =>
      matchesAsyncRequestStatus(template.asynchronousQuestionnaireUsage, asyncRequestStatus, isAsyncPrescription),
    )
    .flatMap((template) =>
      template.languages
        .filter((translation) => translation.language === language)
        .map<TreatmentPlanTemplateStoreData>((translation) => ({
          ...translation,
          id: template.id,
          name: template.name,
          amountOfDays: template.amountOfDays,
          asynchronousQuestionnaireUsage: template.asynchronousQuestionnaireUsage,
        })),
    )
    .filter(matchesSearch)
    .sort((a, b) => a.title.localeCompare(b.title))

  if (eligibleSofiaSuggestions.length === 0 && !hasRegularTemplates) {
    return (
      <div
        className="text-on-surface-subdued relative cursor-default px-2 py-1 text-sm select-none"
        data-testid="nothing_found_treatment_plan_template">
        {t("common.nothingFound")}
      </div>
    )
  }

  return (
    <div data-testid="sofia-supported-suggestions-section">
      <div className="text-on-surface-subdued px-2 pb-2 text-sm leading-5 font-medium">
        {t("episodeOfCare.progressNote.sofiaSupportedSuggestions")}
      </div>
      {eligibleSofiaSuggestions.length > 0 ? (
        eligibleSofiaSuggestions.map(renderTemplateItem)
      ) : (
        <div className="text-on-surface px-2 py-1 text-sm leading-5" data-testid="no-sofia-suggestions">
          {t("episodeOfCare.progressNote.noSofiaSuggestions")}
        </div>
      )}
    </div>
  )
}

const TreatmentPeriod = ({
  value,
  onChange,
  onClose,
  error,
}: {
  value: DateRange
  onChange: (value: DateRange) => void
  onClose?: () => void
  error?: string
}) => {
  const { t } = useTranslation()

  const treatmentPeriod: DateRange = {
    from: new Date(value.from!),
    to: value.to ? new Date(value.to) : undefined,
  }
  const handleChange = (selectedRange: DateRange | undefined) => {
    onChange(selectedRange ?? { from: new Date() })
  }

  return (
    <DateRangePicker
      label={`${t("episodeOfCare.progressNote.treatmentPlan.treatmentPeriod")}*`}
      dataTestId="treatment-plan-period-section"
      value={treatmentPeriod}
      onChange={handleChange}
      onClose={onClose}
      data-testid="treatment-plan-period"
      showFooter={false}
      error={error}
    />
  )
}

const TreatmentPlanReadonlySection = ({ treatmentPlan }: { treatmentPlan: TreatmentPlan }) => {
  const { followUpInstructions, from, recommended, title, to } = treatmentPlan || {}
  const { t } = useTranslation()

  return (
    <ProgressNoteReadOnlyCardSection
      className="border-t"
      testId="treatment-plan-read-only"
      title={t("episodeOfCare.progressNote.treatmentPlan.headerTitle")}>
      <div className="grid gap-4">
        <div className="grid">
          <span className="text-base font-medium" data-testid="title">
            {title}
          </span>
          {from && to && (
            <span className="text-on-surface-subdued text-sm" data-testid="period">{`${t(
              "episodeOfCare.progressNote.treatmentPlan.validFrom",
            )} ${new DateOnly(from).toDisplayableString()} - ${new DateOnly(to).toDisplayableString()}`}</span>
          )}
        </div>
        {recommended && (
          <div className="grid">
            <span className="text-on-surface-subdued text-xs tracking-[0.25px]">
              {t("episodeOfCare.progressNote.treatmentPlan.recommendedTreatment")}
            </span>
            <EditorView
              name="recommended"
              initialValue={recommended}
              onFocus={() => {
                FaroInsightsWrapper.pushEvent("treatment_plan_recommended_focus")
              }}
              onBlur={() => {
                FaroInsightsWrapper.pushEvent("treatment_plan_recommended_blur")
              }}
            />
          </div>
        )}
        {followUpInstructions && (
          <div className="grid">
            <span className="text-on-surface-subdued text-xs tracking-[0.25px]">
              {t("episodeOfCare.progressNote.treatmentPlan.followUpInstructions")}
            </span>
            <EditorView
              name="followUpInstructions"
              initialValue={followUpInstructions}
              onFocus={() => {
                FaroInsightsWrapper.pushEvent("treatment_plan_followup_focus")
              }}
              onBlur={() => {
                FaroInsightsWrapper.pushEvent("treatment_plan_followup_blur")
              }}
            />
          </div>
        )}
      </div>
    </ProgressNoteReadOnlyCardSection>
  )
}

const isTreatmentPlanEmpty = (treatmentPlan: TreatmentPlan) =>
  !treatmentPlan ||
  (isEmptyOrWhitespace(treatmentPlan.followUpInstructions) &&
    isEmptyOrWhitespace(treatmentPlan.recommended) &&
    isEmptyOrWhitespace(treatmentPlan.title) &&
    // if the "from" or the "to" is empty then the period is invalid, thus considered empty
    !isValidDateRange(treatmentPlan.from, treatmentPlan.to))

const appendTemplateValue = (currentValue: string | null, addedValue: string | null): string => {
  const normalizedCurrentValue = currentValue?.trim() ?? ""
  const normalizedAddedValue = addedValue?.trim() ?? " "

  return normalizedCurrentValue.length === 0
    ? normalizedAddedValue
    : `${normalizedCurrentValue}\n\n\n\n${normalizedAddedValue}`
}

export { isTreatmentPlanEmpty, TreatmentPlanEditableSection, TreatmentPlanReadonlySection, appendTemplateValue }
